import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useAuth } from '../providers/AuthProvider';
import { 
  ArrowLeft, Trophy, Coins, Crown, Flame, Zap, 
  Medal, Star, TrendingUp, Users
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeaderboardPage = () => {
  const navigate = useNavigate();
  const { walletAddress, isAuthenticated } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('strength'); // strength, tokens, wins
  const [myRank, setMyRank] = useState(null);
  const wsRef = useRef(null);

  useEffect(() => {
    const setupWebSocket = () => {
      const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
      
      try {
        wsRef.current = new WebSocket(`${wsUrl}/ws/leaderboard`);
        
        wsRef.current.onmessage = (event) => {
          const data = JSON.parse(event.data);
          if (data.type === 'leaderboard_update') {
            setLeaderboard(data.leaderboard);
            if (walletAddress) {
              const myPosition = data.leaderboard.findIndex(p => p.wallet_address === walletAddress);
              setMyRank(myPosition >= 0 ? myPosition + 1 : null);
            }
          }
        };

        wsRef.current.onerror = () => {
          // Fallback to polling if WebSocket fails
        };
      } catch (error) {
        console.error('WebSocket error:', error);
      }
    };

    const fetchLeaderboard = async () => {
      try {
        const response = await axios.get(`${API}/leaderboard?sort_by=${activeTab}&limit=100`);
        setLeaderboard(response.data.players || []);
        
        if (walletAddress) {
          const myPosition = (response.data.players || []).findIndex(p => p.wallet_address === walletAddress);
          setMyRank(myPosition >= 0 ? myPosition + 1 : null);
        }
      } catch (error) {
        console.error('Error fetching leaderboard:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchLeaderboard();
    setupWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [activeTab, walletAddress]);

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-300" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-amber-600" />;
    return <span className="font-anton text-lg text-white/60">#{rank}</span>;
  };

  const getRankBg = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-500/30 to-yellow-600/10 border-yellow-500';
    if (rank === 2) return 'bg-gradient-to-r from-gray-400/20 to-gray-500/10 border-gray-400';
    if (rank === 3) return 'bg-gradient-to-r from-amber-600/20 to-amber-700/10 border-amber-600';
    return 'bg-black/40 border-white/10';
  };

  const formatAddress = (address) => {
    if (!address) return 'Anonymous';
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden pb-20">
      {/* Background */}
      <div 
        className="fixed inset-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(#FFD70010 1px, transparent 1px),
            linear-gradient(90deg, #FFD70010 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-yellow-500/10 rounded-full blur-[200px] pointer-events-none" />

      {/* Header */}
      <header className="relative z-50 pt-8 pb-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <Button
              onClick={() => navigate('/')}
              className="bg-transparent border-2 border-white/20 text-white hover:border-white hover:bg-white/10 font-space uppercase"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>

            {myRank && (
              <motion.div
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-neon-pink/20 border-2 border-neon-pink"
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Trophy className="w-5 h-5 text-neon-pink" />
                <span className="font-anton text-xl text-neon-pink">YOUR RANK: #{myRank}</span>
              </motion.div>
            )}
          </div>

          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <motion.div
              className="inline-block"
              animate={{ rotate: [0, 5, -5, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Trophy className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            </motion.div>
            <h1 
              className="font-anton text-5xl md:text-7xl uppercase"
              style={{
                background: 'linear-gradient(180deg, #FFD700 0%, #FF6600 50%, #FF0066 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              LEADERBOARD
            </h1>
            <p className="font-space text-base text-white/60 mt-4">
              TOP SLAPPERS • REAL-TIME RANKINGS
            </p>
          </motion.div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <div className="flex justify-center gap-2 mb-8">
          {[
            { id: 'strength', label: 'Degen Strength', icon: Zap },
            { id: 'tokens', label: 'Tokens', icon: Coins },
            { id: 'wins', label: 'PvP Wins', icon: Flame },
          ].map((tab) => (
            <Button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`font-anton text-sm uppercase px-4 py-2 ${
                activeTab === tab.id
                  ? 'bg-yellow-500 text-black shadow-[0_0_20px_rgba(255,215,0,0.5)]'
                  : 'bg-black/40 border border-white/20 text-white/60'
              }`}
            >
              <tab.icon className="w-4 h-4 mr-1" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Stats Bar */}
        <motion.div
          className="flex justify-center gap-8 mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="text-center">
            <p className="font-space text-xs text-white/40">TOTAL PLAYERS</p>
            <p className="font-anton text-2xl text-white flex items-center gap-1">
              <Users className="w-5 h-5" />
              {leaderboard.length}
            </p>
          </div>
          <div className="text-center">
            <p className="font-space text-xs text-white/40">LIVE UPDATES</p>
            <motion.p
              className="font-anton text-2xl text-neon-green flex items-center gap-1"
              animate={{ opacity: [1, 0.5, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
            >
              <TrendingUp className="w-5 h-5" />
              REAL-TIME
            </motion.p>
          </div>
        </motion.div>

        {/* Leaderboard List */}
        <div className="space-y-3">
          <AnimatePresence>
            {leaderboard.map((player, index) => {
              const rank = index + 1;
              const isMe = player.wallet_address === walletAddress;
              
              return (
                <motion.div
                  key={player.wallet_address || index}
                  className={`flex items-center gap-4 p-4 rounded-xl border-2 ${getRankBg(rank)} ${
                    isMe ? 'ring-2 ring-neon-pink ring-offset-2 ring-offset-black' : ''
                  }`}
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  layout
                >
                  {/* Rank */}
                  <div className="w-12 flex justify-center">
                    {getRankIcon(rank)}
                  </div>

                  {/* Player Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-mono text-sm text-white">
                        {formatAddress(player.wallet_address)}
                      </p>
                      {isMe && (
                        <span className="px-2 py-0.5 bg-neon-pink/30 rounded text-xs font-bold text-neon-pink">
                          YOU
                        </span>
                      )}
                    </div>
                    <p className="font-space text-xs text-white/40">
                      {player.cards_owned || 0} cards • {player.sets_completed?.length || 0} sets
                    </p>
                  </div>

                  {/* Score */}
                  <div className="text-right">
                    {activeTab === 'strength' && (
                      <div className="flex items-center gap-2">
                        <Zap className="w-5 h-5 text-purple-400" />
                        <span className="font-anton text-2xl text-purple-400">
                          {player.slap_power || 0}
                        </span>
                      </div>
                    )}
                    {activeTab === 'tokens' && (
                      <div className="flex items-center gap-2">
                        <Coins className="w-5 h-5 text-yellow-400" />
                        <span className="font-anton text-2xl text-yellow-400">
                          {(player.coins || 0).toLocaleString()}
                        </span>
                      </div>
                    )}
                    {activeTab === 'wins' && (
                      <div className="flex items-center gap-2">
                        <Flame className="w-5 h-5 text-orange-400" />
                        <span className="font-anton text-2xl text-orange-400">
                          {player.pvp_wins || 0}
                        </span>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>

          {leaderboard.length === 0 && !loading && (
            <div className="text-center py-16">
              <Trophy className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <p className="font-anton text-2xl text-white/40">NO RANKINGS YET</p>
              <p className="font-space text-sm text-white/30 mt-2">Be the first to compete!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaderboardPage;
